import "./Sales.scss";
import "../../assets/styles/Elements.scss";

import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
} from "@material-ui/core";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import CloseIcon from '@material-ui/icons/Close';
import Alert from "@material-ui/lab/Alert";
import axios from 'axios';
import { ApiErrors } from "common/backend/api-errors/errors";
import { SignedUrl } from "common/backend/generated/signed-url-model";
import { CommonButton, CommonFileUpload, CommonToast } from "common/components";
import { SaleInfoField } from "common/components/SaleInfoField";
import { CreateSale, GenerateUploadSignedURL } from "common/graphql/queryDescriptors";
import { CreateFileManageable, GenerateSignedUrlManageable } from "common/store/file/types";
import { useCustomQuery } from "common/store/hooks/useCustomQuery";
import { CreateSaleManageable } from "common/store/sale/types";
import { errorMessages } from "common/utils/validation";
import Navbar from 'components/Navbar';
import { Form, Formik, FormikErrors } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export const Sales = () => {
  // State
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [message, setMessage] = useState("");
  const [toastOpen, setToastOpen] = React.useState(false);
  const [values, setValues] = React.useState<CreateSaleManageable | undefined>(undefined);

  // Hooks
  const { t } = useTranslation();

  const {
    execute: createSale, loading: createSaleLoading
  } = useCustomQuery(CreateSale({
    onError: (error: ApiErrors) => {
      switch (error) {
        case ApiErrors.SALE_NAME_TAKEN:
          setMessage("Sale with given name already exists");
          break;
        default:
          setMessage(error);
          break;
      }

      setShowErrorAlert(true);
    },
    onSuccess: () => {
      setToastOpen(true);
    },
  }));

  const {
    execute: generateUploadSignedURL, loading: generateSignedUploadURLLoading
  } = useCustomQuery(GenerateUploadSignedURL({
    onSuccess: async (data: Array<SignedUrl>) => {
      if (!values || !values.filesObj || values.filesObj.length <= 0 || data.length !== values.filesObj.length) {
        return;
      }

      for (let i = 0; i < data.length; i++) {
        const url = data[i].url;
        const file = values.filesObj[i];
        await axios.put(
          url,
          file,
          {
            headers: { 'Content-Type': file.type || 'text/plain' },
            /* TODO(Marko):
              onUploadProgress: function (progressEvent) {
                console.log('progressEvent: ', progressEvent);
              },
              */
          }
        );
      }

      setValues(new CreateSaleManageable({
        ...values,
        filesObj: [],
        images: data.map((
          signedUrl: SignedUrl, index: number
        ) => new CreateFileManageable({
          id: signedUrl.fileId,
          isFeatured: index === 0
        }).serialize()),
      }));
    },
  }));

  // Option
  const initialValues = new CreateSaleManageable();

  const excludedFields = ["filesObj", "images"];
  const displayedFields = Object.keys(initialValues).filter((field) => !excludedFields.includes(field));

  // Validation
  const validate = (values: CreateSaleManageable): FormikErrors<any> => {
    return new CreateSaleManageable(values).validate(
      Object.keys(values),
      errorMessages
    );
  }

  const onSubmit = async (
    values: CreateSaleManageable, { setSubmitting }: any
  ) => {
    setSubmitting(true);

    if (values.filesObj && values.filesObj.length > 0) {
      setValues(values);
    } else {
      await createSale({ variables: { input: new CreateSaleManageable(values).serialize() } });
    }

    setSubmitting(false);
  }

  useEffect(
    () => {
      if (values) {
        if (values.filesObj && values.filesObj.length > 0) {
          generateUploadSignedURL({ variables: { input: new GenerateSignedUrlManageable({ mimeTypes: values.filesObj.map((file: any) => file.type) }).serialize() } });
        } else if (values.images && values.images.length > 0) {
          createSale({ variables: { input: new CreateSaleManageable(values).serialize() } });
          setValues(undefined);
        }
      }
    },
    [createSale, generateUploadSignedURL, values]
  );

  return (
    <>
      <Navbar />
      <Box className="container">
        <Typography className="title" variant="h2">
            Asset Creation
        </Typography>

        {showErrorAlert ?
          <>
            <Alert
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setShowErrorAlert(false);
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {message}
            </Alert>
            <br />
          </>
          :
          null
        }

        <Formik
          initialValues={initialValues}
          validate={validate}
          onSubmit={onSubmit}
        >
          {(props) =>
            <Form style={{ width: "100%" }}>
              <IconButton
                className="secondary--small autofill"
                onClick={() => props.setValues(CreateSaleManageable.automatic())}
              >
                <AutorenewIcon />
              </IconButton>

              <Grid container direction="row" spacing={4}>
                {displayedFields.map((
                  field, i
                ) =>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    key={i}
                  >
                    <SaleInfoField
                      id={field}
                      {...props}
                    />
                  </Grid>)}
              </Grid>

              {props.isSubmitting && <LinearProgress />}
              <br />

              <Grid container item direction="row" style={{ display: "flex" }}>
                <Paper>
                  <CommonFileUpload
                    id="filesObj"
                    values={props.values}
                    errors={props.errors}
                    touched={props.touched}
                    setFieldValue={props.setFieldValue}
                    buttonText={t("Upload images")}
                    stateText={t("images uploaded")}
                  />
                </Paper>
              </Grid>

              <Grid container item direction="row" justify="flex-end" style={{ display: "flex" }}>
                <Grid item style={{ width: "25%" }}>
                  {createSaleLoading || generateSignedUploadURLLoading ?
                    <CircularProgress />
                    :
                    <CommonButton
                      onClick={props.submitForm}
                      disabled={props.isSubmitting}
                      values={props.values}
                      errors={props.errors}
                      touched={props.touched}
                      text={"Submit"}
                    />
                  }
                </Grid>
              </Grid>
            </Form>
          }
        </Formik>

        <CommonToast
          open={toastOpen}
          onClose={() => setToastOpen(false)}
          text={t("Sale created!")}
          severity={"success"}
        />
      </Box>
    </>
  );
};
